import React from "react";

import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Error, LinkRounded, PeopleAlt, PieChart, Place } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

import { AudienceCardMenu } from "./AudienceCardMenu";
import { HelpBox } from "@/components/atoms";
import { useAudienceLinks } from "@/hooks";
import { Audience } from "@/types";
import { abbreviateNumber, getAudienceIcon, reformatDate } from "@/utils";

interface AudienceCardProps {
  audience: Audience;
}

const AudienceCard: React.FC<AudienceCardProps> = ({
  audience,
}) => {
  const theme = useTheme();
  const links = useAudienceLinks(audience);
  const sharedOverlay = alpha(theme.palette.primary.main, 0.051);

  const isSharedAudience = audience.shared;
  const numSharedWith =
    (audience.shared_with && audience.shared_with.length) || 0;

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "220px",
        backgroundImage: isSharedAudience
          ? `linear-gradient(${sharedOverlay}, ${sharedOverlay})`
          : "var(--Paper-overlay)",
      }}
    >
      <CardActionArea onClick={links.open}>
        <CardHeader
          avatar={
            isSharedAudience ? (
              <HelpBox title={"This audience is shared with you"}>
                <LinkRounded color="primary" />
              </HelpBox>
            ) : audience.shared_with && numSharedWith > 0 ? (
              <HelpBox
                title={`This audience is shared with ${numSharedWith} user${
                  numSharedWith > 1 ? "s" : ""
                }`}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    background: alpha(theme.palette.info.main, 0.5),
                    width: 20,
                    height: 20,
                    fontSize: "12px",
                    m: "2px",
                  }}
                >
                  {numSharedWith}
                </Avatar>
              </HelpBox>
            ) : (
              <Box height={24} />
            )
          }
          action={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 20,
                height: 20,
                m: 1,
              }}
            >
              {audience.status === 'processing' ? (
                <HelpBox title="Audience being processed">
                  <CircularProgress size={20} color="info"/>
                </HelpBox>
              ) : audience.status === 'error' ? (
                <HelpBox title="There was a problem with this audience">
                  <Error color="error" style={{ fontSize: 20 }}/>
                </HelpBox>
              ) : (
                audience.audience_type === 'upload' ? (
                  getAudienceIcon(audience.upload_mode)
                ): null
              )}
            </Box>
          }
          sx={{ p: 1, pb: 0 }}
        />
        <CardContent
          sx={{
            height: "120px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
            alignItems: "center",
            pt: 0,
          }}
        >
          <Typography
            variant="h6"
            textAlign="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              maxWidth: "150px",
            }}
          >
            {audience.audience_name}
          </Typography>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-evenly"
            width="100%"
            mt={2}
          >
            {audience.audience_size && audience.audience_size.length > 1 ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-evenly"
              >
                <PieChart fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1" fontWeight={600}>
                  {audience.audience_size.length} segments
                </Typography>
              </Box>
            ) : (
              <>
                {audience.audience_size &&
                  audience.audience_size[0]?.num_postcodes && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Place fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].num_postcodes
                        )}
                      </Typography>
                    </Box>
                  )}
                {audience.audience_size &&
                  audience.audience_size[0]?.num_sectors && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Place fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].num_sectors
                        )}
                      </Typography>
                    </Box>
                  )}
                {audience.audience_size &&
                  audience.audience_size[0]?.population_count && (
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <PeopleAlt fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        {abbreviateNumber(
                          audience.audience_size[0].population_count
                        )}
                      </Typography>
                    </Box>
                  )}
              </>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
      <CardActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pl: 2,
        }}
      >
        <Box>
          {audience.created_at && (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              mt={1}
            >
              {reformatDate(audience.created_at)}
            </Typography>
          )}
        </Box>
        <AudienceCardMenu audience={audience} />
      </CardActions>
    </Card>
  );
};

export default AudienceCard;
